import storeManager from '../store/StoreManager';
import testQuestionsActions from '../actions/TestQuestionsActions';
import sessionReader from '../store/readHelpers/SessionReader';
import assessmentReader from '../store/readHelpers/AssessmentReader';
import uiService from './UIService';
import scorableItemNumberingService from './questionView/ScorableItemNumberingService';
import preCheckService from './PreCheckService';
import cacheManager from '../loading/resources/cacheManager';
import tocReader from '../store/readHelpers/TocReader';
import navigationService from './NavigationService';
import parentTestService from './ParentTestService';
import stateCacheService from './StateCache';
import backgroundAudioService from './BackgroundAudioService';
import { stringifyError } from './ErrorReporting';

const reloginService = {

    isReloginRulesContext() {
        return sessionReader.isRestoreLastVisitedQuestionEnabled() && sessionReader.isContextExam();
    },

    isResumedTestWithReloginRules() {
        return reloginService.isReloginRulesContext() && sessionReader.isResumed();
    },

    shouldStoreNextQuestion() {
        return reloginService.isReloginRulesContext() && !sessionReader.isContextReadinessCheck();
    },

    isReloginRedirectAllowed() {
        // if the test has relogin rules enabled and either it's using lockdown context or has already been resumed by a password
        return reloginService.isResumedTestWithReloginRules() && (
            !sessionReader.isTestConfiguredToRequireInvigilatorPassword() || sessionReader.isResumedTestAfterPassword()
        );
    },

    shouldStoreNextQuestionIdIfHorizontalLayoutConditions() {
        try {
            const { currentlyShown } = storeManager.getState();
            const currentSection = assessmentReader.getCurrentShownSection();
            if (currentSection && !uiService.isVerticalLayout(currentSection)) {
                console.log(`Horizontal layout when relogin rules enabled, questionId: ${currentlyShown.questionId}`);
                if (scorableItemNumberingService.useNumberPerInteraction() && currentlyShown.scorableItem) {
                    const lastItem = scorableItemNumberingService.getLastScorableItemInQuestion(currentlyShown.questionId);
                    console.log(`Horizontal layout when relogin rules enabled and uses number per interaction, 
                    questionId: ${currentlyShown.questionId}, scorableItem: ${currentlyShown.scorableItem},
                    last item contentRevisionId: ${lastItem.contentRevisionId}, last item orderNumber: ${lastItem.orderNumber}`);
                    return currentlyShown.questionId === lastItem.contentRevisionId && currentlyShown.scorableItem === lastItem.orderNumber;
                }
                return true;
            }
        } catch (e) {
            console.error('Was not able to return value for shouldStoreNextQuestionIdIfHorizontalLayoutConditions', stringifyError(e));
        }
        console.log('Returned false value for shouldStoreNextQuestionIdIfHorizontalLayoutConditions');
        return false;
    },

    shouldStoreNextQuestionIdIfVerticalLayoutConditions(locationData = {}) {
        try {
            const currentSection = assessmentReader.getCurrentShownSection();
            if (currentSection && uiService.isVerticalLayout(currentSection)) {
                const { currentlyShown } = storeManager.getState();
                const currentSectionIndex = assessmentReader.getCurrentShownSectionIndex();
                const isFirstQuestionInSection = assessmentReader.isFirstQuestionInSection();
                const isLastQuestionInSection = assessmentReader.isLastQuestionInSection();
                const isLastSectionInAssessment = assessmentReader.isLastSectionInAssessment();
                if (isLastSectionInAssessment && isLastQuestionInSection) {
                    return false;
                }
                // case when TT is navigating forward from one section to another
                if (isLastQuestionInSection) {
                    console.log(`Should return value for vertical layout when relogin rules enabled for, questionId: ${currentlyShown.questionId}, 
                        currentlyShown scorableItem: ${currentlyShown.scorableItem}, when is last question in section`);
                    if (scorableItemNumberingService.useNumberPerInteraction() && currentlyShown.scorableItem) {
                    // if vertical layout and if test is using useNumberPerInteraction, we want to allow storage of next question in state only if
                    // we are on the last question in the section and on the last interaction in the question and the TT navigates forward
                        const lastItem = scorableItemNumberingService.getLastScorableItemInQuestion(currentlyShown.questionId);
                        console.log(`Vertical layout when relogin rules enabled, number per interaction for, lastItem: ${lastItem.contentRevisionId}, 
                            lastItem orderNumber: ${lastItem.orderNumber}, when is last question in section and is using Number Per Interaction`);
                        return currentlyShown.questionId === lastItem.contentRevisionId && currentlyShown.scorableItem === lastItem.orderNumber;
                    }
                    return true;
                }
                // case when TT is navigating back from one section to another
                if (isFirstQuestionInSection && currentSectionIndex !== 0) {
                    console.log(`Should return value for vertical layout when relogin rules enabled for, questionId: ${currentlyShown.questionId}, 
                    currentlyShown scorableItem: ${currentlyShown.scorableItem}, when is first question in section: ${currentSectionIndex}`);
                    if (scorableItemNumberingService.useNumberPerInteraction() && currentlyShown.scorableItem) {
                    // if vertical layout and if test is using useNumberPerInteraction, we want to allow storage of next question in state only if
                    // we are on the first question in the section and on the first interaction in the question and the TT navigates back
                        console.log(`Should return value for vertical layout when relogin rules enabled for, 
                        locationData: ${locationData.contentRevisionId},  locationData orderNumber: ${locationData.orderNumber}, 
                       when is first question in section and is using Number Per Interaction`);
                        return currentlyShown.questionId !== locationData.contentRevisionId && currentlyShown.scorableItem > locationData.orderNumber;
                    }
                    return true;
                }
            }
        } catch (e) {
            console.error('Was not able to return value for shouldStoreNextQuestionIdIfVerticalLayoutConditions', stringifyError(e));
        }
        console.log('Returned false value for shouldStoreNextQuestionIdIfVerticalLayoutConditions');
        return false;
    },

    storeNextQuestionIdFlag(questionId, nextQuestionRevisionId) {
        storeManager.dispatch(
            testQuestionsActions.setNextQuestionId(questionId, nextQuestionRevisionId)
        );
    },

    // function used to call the navigation method to navigate the TT back to last visited question for a standalone test, if the test
    // has readiness check enabled and completed readiness check and if the caching is done for test resources.
    navigateToResumedQuestionIfStandaloneTestExposable() {
        try {
            const state = storeManager.getState();
            if (preCheckService.readinessCheckCompleted() && cacheManager.cacheIsDone(state.assessment.assessmentRunId)) {
                const questionIdToNavigateTo = reloginService.getQuestionIdFromResponseStateToNavigateTo();
                if (questionIdToNavigateTo) {
                    const sectionId = tocReader.getSectionIdForQuestionId(questionIdToNavigateTo);
                    console.log(`Params to redirect after relogin, standalone, questionId: ${questionIdToNavigateTo}, and section id: ${sectionId}`);
                    if (sectionId) {
                        reloginService.navigateToQuestionAfterRelogin(questionIdToNavigateTo, sectionId);
                        return true;
                    }
                }
            }
        } catch (e) {
            console.error('Unable to navigate to question after relogin, standalone tests', stringifyError(e));
        }
        console.log('Returned false value for navigateToResumedQuestionIfStandaloneTestExposable');
        return false;
    },

    navigateAfterReadinessCheckCompleted() {
        if (sessionReader.isParentTest()) {
            return parentTestService.startInProgressChildTestAfterReadinessCheckCompleted();
        }
        return reloginService.navigateToResumedQuestionIfStandaloneTestExposable();
    },

    // function will provide the path to resume the TT to latest visited question and navigate the TT to that question, if test is using
    // number per interaction, path will be build using scorable items
    navigateToQuestionAfterRelogin(questionId, sectionId) {
        try {
            let path = navigationService.getPathForQuestion(questionId, sectionId);
            const currentSection = assessmentReader.getCurrentShownSection();
            if (scorableItemNumberingService.useNumberPerInteraction() && !uiService.isVerticalLayout(currentSection)) {
                const scorableItemOrderNumber = scorableItemNumberingService.getFirstValidOrderNumberInQuestion(questionId);
                path = navigationService.getPathForScorableItem(sectionId, questionId, scorableItemOrderNumber);
            }
            console.log(`Path to redirect to after relogin, path: ${path}`);
            return navigationService.navigateToPath(path);
        } catch (e) {
            console.error('Unable to navigate to question after relogin: ', stringifyError(e));
        }
        console.log('Returned false value for navigateToQuestionAfterRelogin');
        return false;
    },

    // function used to navigate the TT to either latest visited question if basic layout or if last visited section if vertical layout
    navigateAfterChildTestStartAfterRelogin() {
        try {
            const questionIdToNavigateTo = reloginService.getQuestionIdFromResponseStateToNavigateTo();
            console.log(`Params to redirect after relogin, child tests, questionId: ${questionIdToNavigateTo}`);
            if (questionIdToNavigateTo) {
                const sectionId = tocReader.getSectionIdForQuestionId(questionIdToNavigateTo);
                if (sectionId) {
                    console.log(`Params to redirect after relogin, child tests, and section id: ${sectionId}`);
                    const isBasicLayout = !uiService.isVerticalLayout(assessmentReader.getSectionById(sectionId));
                    if (isBasicLayout) {
                        console.log('navigateAfterChildTestStartAfterRelogin - trying to navigate to question');
                        return navigationService.navigateToQuestion(questionIdToNavigateTo, sectionId, isBasicLayout);
                    }
                    console.log('navigateAfterChildTestStartAfterRelogin - trying to navigate to section');
                    return navigationService.navigateToSection(sectionId);
                }
            }
        } catch (e) {
            console.error('Unable to navigate to question after relogin: ', stringifyError(e));
        }
        console.log('Returned false value for navigateAfterChildTestStartAfterRelogin');
        return false;
    },

    shouldRefreshUserTestSetupUponBackOnline() {
        return sessionReader.hasEvictionBlockageReasonForTest() || sessionReader.shouldStopTimeUponExit();
    },

    getQuestionIdFromResponseStateToNavigateTo() {
        try {
            const { assessment, response } = storeManager.getState();

            const responsesArray = Object.keys(response).map(key => response[key]).filter(responseItem => responseItem.datestamp);
            const localStorageLastVisitedQuestion = stateCacheService.getStateCacheInstance()
                .getLastVisitedQuestionObject(assessment.assessmentRunId);

            if (!localStorageLastVisitedQuestion && responsesArray.length === 0) {
                return undefined;
            }
            if (localStorageLastVisitedQuestion) {
                console.log(`Stored local storage object - timestampMillis: ${localStorageLastVisitedQuestion.timestampMillis}, 
                identifier : ${localStorageLastVisitedQuestion.identifier}`);
            }

            let latestStoredResponseObject = null;
            if (responsesArray.length !== 0) {
                latestStoredResponseObject = responsesArray.reduce(
                    (acc, currentValue) => (new Date(acc.datestamp).getTime() > new Date(currentValue.datestamp).getTime() ? acc : currentValue)
                );
                console.log(`Stored response object: ${latestStoredResponseObject.datestamp}, ${latestStoredResponseObject.nextQuestionId}`);
            }

            const latestExposedQuestion = reloginService.compareLocalStorageObjectWithResponseObject(latestStoredResponseObject,
                localStorageLastVisitedQuestion);

            if (latestExposedQuestion.nextQuestionId) {
                console.log(`Next question id to navigate to after relogin: ${latestExposedQuestion.nextQuestionId}`);
                return latestExposedQuestion.nextQuestionId;
            }
            console.log(`Identifier to navigate to after relogin: ${latestExposedQuestion.identifier}`);
            return latestExposedQuestion.identifier;
        } catch (e) {
            console.error('Unable to return question id to navigate to after relogin: ', stringifyError(e));
        }
        console.log('Returned false value for getQuestionIdFromResponseStateToNavigateTo');
        return false;
    },

    compareLocalStorageObjectWithResponseObject(latestStoredResponseObject, localStorageStoredObject) {
        if (!latestStoredResponseObject?.datestamp && !localStorageStoredObject?.timestampMillis) {
            return undefined;
        }
        if (latestStoredResponseObject?.datestamp) {
            console.log(`compareLocalStorageObjectWithResponseObject-latestStoredResponseObject.datestamp: ${latestStoredResponseObject.datestamp}`);
        }
        if (localStorageStoredObject?.timestampMillis) {
            console.log(`compareLocalStorageObjectWithResponseObject-localStorageObject.timestampMilli: ${localStorageStoredObject.timestampMillis}`);
        }
        if ((latestStoredResponseObject && localStorageStoredObject &&
                (new Date(latestStoredResponseObject.datestamp).getTime() > localStorageStoredObject.timestampMillis)) ||
            (latestStoredResponseObject && !localStorageStoredObject)) {
            return latestStoredResponseObject;
        }
        return localStorageStoredObject;
    },

    redirectedResumedTestAfterPasswordInReloginRulesContext() {
        if (reloginService.isResumedTestWithReloginRules()) {
            if (sessionReader.isParentTest()) {
                console.log(`Calling startInProgressChildTestIfReadinessCheckCompletedAndTestCached in 
                startInProgressChildTestIfReadinessCheckCompletedAndTestCached`);
                parentTestService.startInProgressChildTestIfReadinessCheckCompletedAndTestCached();
                return true;
            }
            const didAutoNavigateToLastVisitedPageInStandAloneTest = reloginService.navigateToResumedQuestionIfStandaloneTestExposable();
            if (didAutoNavigateToLastVisitedPageInStandAloneTest) {
                console.log(`Called navigateToResumedQuestionIfStandaloneTestExposable in 
                startInProgressChildTestIfReadinessCheckCompletedAndTestCached`);
                if (assessmentReader.hasBackgroundAudioResource()) {
                    backgroundAudioService.playAudioForNonChildTest();
                }
                return true;
            }
        }
        console.log('Returned false value for redirectedResumedTestAfterPasswordInReloginRulesContext');
        return false;
    },
};

export default reloginService;
